import { isEmpty, sortBy } from "lodash/fp";

import { get, patch } from "@readai/common/api";
import type {
  ApiCreateSessionNoteOperation,
  ApiDeleteSessionNoteOperation,
  ApiSessionNote,
  ApiSessionNoteOperation,
  ApiUpdateSessionNoteOperation,
  SessionNote,
  SessionNoteCreateBody,
  SessionNoteUpdateBody,
} from "@readai/common/types";
import { optionalParseUTCDateTimeFromString } from "@readai/common/utils/datetimeUtils";

/** Creates, updates, and/or deletes notes for a given session */
export const patchNotes = async (
  sessionId: string,
  operations: ApiSessionNoteOperation[],
): Promise<void> => {
  if (isEmpty(operations)) return;
  return patch(`/sessions/${sessionId}/notes`, operations, {
    withCreds: true,
  });
};

const parseNotes = (notes: ApiSessionNote[]): SessionNote[] => {
  const parsedNotes: SessionNote[] = (notes ?? []).map((note) => ({
    ...note,
    isDeleted: Boolean(note.isDeleted),
    isEdited: Boolean(note.isEdited),
    value: note.text,
    startTime: optionalParseUTCDateTimeFromString(note.startTime),
    endTime: optionalParseUTCDateTimeFromString(note.endTime),
  }));

  return sortBy((n) => n.startTime?.toMillis(), parsedNotes);
};

/** Fetches all notes for a single session */
export const getNotes = async (sessionId: string): Promise<SessionNote[]> => {
  const notes = await get(`/sessions/${sessionId}/notes`, { withCreds: true });
  return parseNotes(notes);
};

/** Creates a single note for a given session */
export const createNote = async (
  sessionId: string,
  noteBody: SessionNoteCreateBody,
) => {
  const operation: ApiCreateSessionNoteOperation = {
    operation: "create",
    sessionId,
    text: noteBody.text,
    startTime: noteBody.createTime,
    endTime: noteBody.createTime,
    private: noteBody.private ?? true,
  };

  return patchNotes(sessionId, [operation]);
};

/** Updates a single note for a given session */
export const updateNote = (sessionId: string, note: SessionNoteUpdateBody) => {
  const operation: ApiUpdateSessionNoteOperation = {
    operation: "update",
    id: note.id,
    text: note.text,
    private: note.private,
  };

  return patchNotes(sessionId, [operation]);
};

/** Deletes a single note for a given session */
export const deleteNote = (sessionId: string, id: ApiSessionNote["id"]) => {
  const operation: ApiDeleteSessionNoteOperation = {
    operation: "delete",
    id,
  };

  return patchNotes(sessionId, [operation]);
};
